/* ============================================================
   Mason Video Hero — scroll-controlled video.

   Default behaviour: the vertical scroll position inside the hero
   section drives the video timeline directly (scrubbing) and
   crossfades the text scenes.

   Fallbacks:
   - Reduced motion: no scrubbing/playback; poster frame stays,
     scenes still crossfade on scroll (calm, no video motion).
   - Touch / coarse pointer / small screens: normal muted autoplay
     loop, while scenes remain scroll-linked so it still feels
     intentional (scrubbing video is unreliable on mobile Safari).
   ============================================================ */
(function () {
  function initHero(root) {
    if (!root || root.dataset.masonVideoHeroInit === "true") return;
    root.dataset.masonVideoHeroInit = "true";

    var stage = root.querySelector(".mason-video-hero__stage");
    var video = root.querySelector(".mason-video-hero__video");
    var scenes = Array.prototype.slice.call(
      root.querySelectorAll(".mason-video-hero__scene")
    );
    var progressBar = root.querySelector(".mason-video-hero__progress-bar");

    if (!stage) return;

    var reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    var canScrub =
      !!video &&
      !reduceMotion &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
      window.innerWidth >= 990;

    // ---- Fallback playback for non-scrub contexts ----
    if (video && !canScrub) {
      if (reduceMotion) {
        // Keep the first frame as a still image; no motion.
        try {
          video.pause();
          video.removeAttribute("autoplay");
        } catch (e) {}
      } else {
        video.muted = true;
        video.loop = true;
        video.setAttribute("playsinline", "");
        var playAttempt = video.play();
        if (playAttempt && typeof playAttempt.catch === "function") {
          playAttempt.catch(function () {});
        }
      }
    } else if (video && canScrub) {
      // Prevent any native playback; we drive currentTime ourselves.
      video.muted = true;
      video.pause();
      video.removeAttribute("autoplay");
    }

    // ---- Scroll progress helpers ----
    var progress = 0; // 0..1 across the hero scroll distance
    var targetTime = 0;
    var currentTime = 0;
    var duration = 0;
    var rafId = null;
    var needsFrame = false;

    function refreshDuration() {
      if (video && isFinite(video.duration) && video.duration > 0) {
        duration = video.duration;
      }
    }

    if (video) {
      if (video.readyState >= 1) refreshDuration();
      video.addEventListener("loadedmetadata", refreshDuration);
      video.addEventListener("durationchange", refreshDuration);
    }

    function computeProgress() {
      var rect = root.getBoundingClientRect();
      var stageHeight = stage.offsetHeight || window.innerHeight;
      var scrollDistance = root.offsetHeight - stageHeight;
      if (scrollDistance <= 0) return 0;
      // rect.top starts at 0 when the section reaches the top of the
      // viewport, then goes negative as we scroll through it.
      var scrolled = -rect.top;
      var p = scrolled / scrollDistance;
      if (p < 0) p = 0;
      if (p > 1) p = 1;
      return p;
    }

    function updateScenes(p) {
      if (!scenes.length) return;
      // Divide progress evenly across scenes and activate the current one.
      var idx = Math.min(
        scenes.length - 1,
        Math.floor(p * scenes.length + 0.0001)
      );
      for (var i = 0; i < scenes.length; i++) {
        if (i === idx) {
          if (!scenes[i].classList.contains("is-active"))
            scenes[i].classList.add("is-active");
        } else if (scenes[i].classList.contains("is-active")) {
          scenes[i].classList.remove("is-active");
        }
      }
    }

    function render() {
      rafId = null;

      // Smoothly ease the video toward the scroll target (no jank).
      if (canScrub && duration > 0 && video) {
        targetTime = progress * (duration - 0.05);
        currentTime += (targetTime - currentTime) * 0.12;
        if (Math.abs(targetTime - currentTime) < 0.01) {
          currentTime = targetTime;
        }
        if (video.readyState >= 2) {
          try {
            video.currentTime = currentTime;
          } catch (e) {}
        }
        // Keep animating until we've settled on the target.
        if (Math.abs(targetTime - currentTime) > 0.01) {
          rafId = window.requestAnimationFrame(render);
          return;
        }
      }

      if (progressBar) {
        progressBar.style.width = (progress * 100).toFixed(2) + "%";
      }

      if (needsFrame) {
        needsFrame = false;
        rafId = window.requestAnimationFrame(render);
      }
    }

    function requestRender() {
      if (rafId === null) {
        rafId = window.requestAnimationFrame(render);
      } else {
        needsFrame = true;
      }
    }

    function onScroll() {
      progress = computeProgress();
      if (progress > 0.02) {
        root.classList.add("is-scrolled");
      } else {
        root.classList.remove("is-scrolled");
      }
      updateScenes(progress);
      requestRender();
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    // Initial paint
    updateScenes(0);
    onScroll();
  }

  function initAll() {
    var heroes = document.querySelectorAll(".mason-video-hero");
    Array.prototype.forEach.call(heroes, initHero);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // Shopify theme editor: re-init when the section is re-rendered.
  document.addEventListener("shopify:section:load", function (e) {
    var hero = e.target.querySelector
      ? e.target.querySelector(".mason-video-hero")
      : null;
    if (hero) {
      hero.dataset.masonVideoHeroInit = "";
      initHero(hero);
    }
  });
})();
